import {
  AiHelpCategory,
  AiHelpPageContext,
  AiHelpQuestion,
} from '@components/ai-help/ai-help.model';

/**
 * Static mock content for the AI Help panel (FEC-1220).
 *
 * This is the UI-only pass — there is no AI backend yet, so every answer here
 * is hand-authored rather than generated. The Browse categories and their
 * question text are taken verbatim from the "Agent Access common questions"
 * doc attached to the ticket; the Apartments quick-actions below it are
 * authored to match the approved Zeplin screens (AI Help / A–E).
 */

// ---------------------------------------------------------------------------
// Apartments page-context questions (Zeplin screens A, B, C)
// ---------------------------------------------------------------------------

const ADD_APARTMENT: AiHelpQuestion = {
  icon: 'plus',
  text: 'How do I add a new apartment?',
  answer: {
    summary: "Apartments are added from the project's Apartments page, it takes about a minute.",
    steps: [
      { title: 'Open the Apartments page', detail: 'In the left menu, click Apartments.' },
      { title: 'Click Add apartment', detail: 'Use the black button at the top right.' },
      {
        title: 'Fill in the details',
        detail: 'Enter the lot number, type, level, price and status, then click Save.',
      },
    ],
    screenshotCaption: 'Apartments › Add apartment (top right)',
    tip: 'You can also import apartments from a spreadsheet using Import.',
    related: ['How do I edit this?', 'What permissions are required?'],
  },
};

const CHANGE_APARTMENT_STATUS: AiHelpQuestion = {
  icon: 'tag',
  text: "How do I change an apartment's status?",
  answer: {
    summary:
      'Apartment status controls what agents and buyers see, and updates instantly across the portal.',
    steps: [
      {
        title: 'Open the apartment',
        detail: 'In the Apartments table, click the lot you want to update.',
      },
      {
        title: 'Change the status',
        detail: 'Use the Status dropdown to choose Available, Held, Reserved, Sold, or Unreleased.',
      },
      { title: 'Save your change', detail: 'Click Save — the new status applies immediately.' },
    ],
    tip: 'Sold and Reserved lots are locked from agent reservation automatically.',
    related: ['What do the different statuses mean?', 'Can agents change a status?'],
  },
};

const BULK_UPDATE_PRICES: AiHelpQuestion = {
  icon: 'dollar-circle',
  text: 'How do I bulk-update apartment prices?',
  answer: {
    summary: 'Bulk price changes are done with a CSV import rather than editing lots one by one.',
    steps: [
      {
        title: 'Export the current list',
        detail: 'On the Apartments page, click Export to download the existing prices as a CSV.',
      },
      {
        title: 'Update the prices',
        detail: 'Edit the price column in the spreadsheet, keeping the lot numbers unchanged.',
      },
      {
        title: 'Import it back',
        detail: 'Click Import, upload the file, and confirm the changes.',
      },
    ],
    tip: 'Import shows a preview before anything is saved, so you can catch mistakes first.',
    related: [
      'What happens if I make a mistake in the import?',
      'Can I import new apartments the same way?',
    ],
  },
};

const AGENT_RESERVE_APARTMENT: AiHelpQuestion = {
  icon: 'user-profile',
  text: 'How does an agent reserve an apartment?',
  answer: {
    summary: 'Agents reserve an available apartment directly from the Apartments page.',
    steps: [
      { title: 'Find the apartment', detail: 'Open Apartments and locate an Available lot.' },
      { title: 'Click Reserve', detail: "Select Reserve from the lot's action menu." },
      {
        title: 'Submit buyer details',
        detail: "Enter the buyer's details and submit — the lot moves to Pending until approved.",
      },
    ],
    tip: 'A reservation holds the lot for the agency until an admin approves or declines it.',
    related: [
      'What happens after a reservation is submitted?',
      'Can an agent cancel a reservation?',
    ],
  },
};

const AGENT_SEES_ON_PAGE: AiHelpQuestion = {
  icon: 'exclamation-circle',
  text: 'What can agents see on this page?',
  answer: {
    summary:
      "Agents see the same Apartments table, but only pricing and status they're permitted to view.",
    steps: [
      { title: 'Lot details', detail: 'Lot number, type, level and status are always visible.' },
      { title: 'Pricing', detail: 'Prices show only for apartments their agency has access to.' },
      {
        title: 'Actions',
        detail: "Agents can Reserve or request a Hold, but can't edit or delete apartments.",
      },
    ],
    related: ["Why can't an agent see a price?", 'How does an agent request a hold?'],
  },
};

const AGENT_REQUEST_HOLD: AiHelpQuestion = {
  icon: 'tag',
  text: 'How does an agent request a hold?',
  answer: {
    summary:
      'A hold reserves a lot temporarily without a full reservation, giving the agent time to confirm with a buyer.',
    steps: [
      { title: 'Open the apartment', detail: 'Find the Available lot on the Apartments page.' },
      { title: 'Select Request hold', detail: "Choose Request hold from the lot's action menu." },
      {
        title: 'Wait for approval',
        detail:
          'An admin approves or declines the hold — approved holds lock the lot for a set period.',
      },
    ],
    related: ['How long does a hold last?', 'How does an agent reserve an apartment?'],
  },
};

const AGENT_SEES_WHEN_SOLD: AiHelpQuestion = {
  icon: 'exclamation-circle',
  text: 'What does an agent see when a lot is sold?',
  answer: {
    summary: 'Once a lot is marked Sold, agents see it greyed out and locked from reservation.',
    steps: [
      {
        title: 'Status badge',
        detail: 'The lot shows a Sold status badge in the Apartments table.',
      },
      {
        title: 'No actions available',
        detail: 'Reserve and Hold options are removed for that lot.',
      },
    ],
    related: ['Can a sold lot be reverted?', 'What do the different statuses mean?'],
  },
};

const AGENT_SUBMIT_EOI: AiHelpQuestion = {
  icon: 'user-profile',
  text: 'How do agents submit an EOI?',
  answer: {
    summary:
      "An Expression of Interest (EOI) is submitted from the apartment's action menu before a full reservation.",
    steps: [
      { title: 'Open the apartment', detail: 'Find the lot on the Apartments page.' },
      {
        title: 'Select Submit EOI',
        detail: "Choose Submit EOI from the action menu and fill in the buyer's details.",
      },
      { title: 'Track it', detail: 'The EOI appears under Requests until an admin actions it.' },
    ],
    related: ['Where can I find an EOI form?', 'How does an agent reserve an apartment?'],
  },
};

const AGENT_WHY_NO_PRICE: AiHelpQuestion = {
  icon: 'dollar-circle',
  text: "Why can't an agent see a price?",
  answer: {
    summary:
      "Pricing is hidden when an agent's agency doesn't have price access for that project, or the lot is unreleased.",
    steps: [
      { title: 'Check the lot status', detail: 'Unreleased lots never show a price to agents.' },
      {
        title: 'Check agency permissions',
        detail: "An admin can grant price visibility under the project's Agencies settings.",
      },
    ],
    related: ['What can agents see on this page?', 'How do I give an agency price access?'],
  },
};

/** Ask/Home tab — "SUGGESTED FOR APARTMENTS" (Zeplin screen A). */
export const APARTMENTS_ADMIN_QUESTIONS: readonly AiHelpQuestion[] = [
  ADD_APARTMENT,
  CHANGE_APARTMENT_STATUS,
  BULK_UPDATE_PRICES,
];

/** Ask/Home tab — "FROM AN AGENT'S VIEW" (Zeplin screen A). */
export const APARTMENTS_ASK_AGENT_PREVIEW: readonly AiHelpQuestion[] = [
  AGENT_RESERVE_APARTMENT,
  AGENT_SEES_ON_PAGE,
];

/** For Agents tab — "AGENTS ON APARTMENTS" (Zeplin screen C). */
export const APARTMENTS_AGENTS_ON_PAGE: readonly AiHelpQuestion[] = [
  AGENT_RESERVE_APARTMENT,
  AGENT_REQUEST_HOLD,
  AGENT_SEES_WHEN_SOLD,
];

/** For Agents tab — "COMMON AGENT QUESTIONS" (Zeplin screen C). */
export const APARTMENTS_COMMON_AGENT_QUESTIONS: readonly AiHelpQuestion[] = [
  AGENT_SUBMIT_EOI,
  AGENT_WHY_NO_PRICE,
];

// ---------------------------------------------------------------------------
// Browse categories — from the "Agent Access common questions" doc (FEC-1220)
// ---------------------------------------------------------------------------

function q(icon: string, text: string, answer: AiHelpQuestion['answer']): AiHelpQuestion {
  return { icon, text, answer };
}

export const AI_HELP_CATEGORIES: readonly AiHelpCategory[] = [
  {
    id: 'getting-started',
    icon: 'exclamation-circle',
    label: 'Getting Started',
    questions: [
      q('key', 'How do I log in to the portal?', {
        summary: 'Sign in with the email and password your admin set up for you.',
        steps: [
          { title: 'Go to the sign-in page', detail: 'Open Agent Access and click Sign in.' },
          {
            title: 'Enter your details',
            detail: 'Type your email and password, then click Sign in.',
          },
          {
            title: 'Complete MFA if asked',
            detail: 'Enter the 6-digit code from your authenticator app.',
          },
        ],
        related: ['I forgot my password. What should I do?', 'How do I change my password?'],
      }),
      q('lock', 'I forgot my password. What should I do?', {
        summary: 'Use Reset your password from the sign-in page — no need to contact support.',
        steps: [
          { title: 'Click Reset your password', detail: 'On the sign-in page, select this link.' },
          { title: 'Check your email', detail: 'Follow the reset link sent to your inbox.' },
          { title: 'Set a new password', detail: 'Choose a new password and sign in.' },
        ],
        related: ['How do I change my password?', 'How do I log in to the portal?'],
      }),
      q('huge-lock', 'How do I change my password?', {
        summary: 'You can update your password any time from My Account.',
        steps: [
          { title: 'Open My Account', detail: 'Click your avatar, top right, then My Account.' },
          { title: 'Go to Password Management', detail: 'Select the Password Management tab.' },
          {
            title: 'Save your new password',
            detail: 'Enter your current and new password, then Save.',
          },
        ],
        related: ['I forgot my password. What should I do?'],
      }),
    ],
  },
  {
    id: 'user-management',
    icon: 'users-twin-profile',
    label: 'User Management',
    questions: [
      q('users-twin-profile', 'How do I add a new user from my agency?', {
        summary: 'Agency admins can add new users from the Users page.',
        steps: [
          { title: 'Open Users', detail: 'Click Users in the main navigation.' },
          { title: 'Click Add user', detail: 'Use the black button at the top right.' },
          {
            title: 'Enter their details',
            detail: "Add their name, email and role, then Save — they'll receive an invite email.",
          },
        ],
        related: [
          'Who can add users within my agency?',
          'How do I remove or deactivate a user with my agency?',
        ],
      }),
      q('exclamation-circle', 'Who can add users within my agency?', {
        summary: 'Only users with the Agency Admin role can add or remove users for their agency.',
        steps: [
          {
            title: 'Check your role',
            detail: 'Your role shows under My Account › Role & Permissions.',
          },
          {
            title: 'Ask an Agency Admin',
            detail: "If you're not an admin, ask your agency's admin to add the user for you.",
          },
        ],
        related: ['How do I add a new user from my agency?'],
      }),
      q('trashIcon', 'How do I remove or deactivate a user with my agency?', {
        summary: 'Deactivating a user removes their access without deleting their history.',
        steps: [
          { title: 'Open Users', detail: 'Click Users in the main navigation.' },
          { title: 'Find the user', detail: 'Search for them in the list.' },
          {
            title: 'Deactivate them',
            detail: "Open their row's action menu and select Deactivate.",
          },
        ],
        related: ['How do I add a new user from my agency?'],
      }),
    ],
  },
  {
    id: 'marketing-materials',
    icon: 'presentation-chart',
    label: 'Marketing Materials',
    questions: [
      q('document', 'Where can I find marketing materials?', {
        summary: "All marketing materials live on each project's Documents page.",
        steps: [
          { title: 'Open the project', detail: 'Go to Projects and select the one you need.' },
          { title: 'Click Documents', detail: 'In the left menu, click Documents.' },
          {
            title: 'Browse by type',
            detail: 'Materials are grouped by type — renders, floorplans, contracts, and more.',
          },
        ],
        related: [
          'What types of marketing materials are available?',
          'How do I download marketing materials?',
        ],
      }),
      q('document', 'Where can I find sales manual?', {
        summary:
          "The sales manual sits alongside the other marketing files on the project's Documents page.",
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          {
            title: 'Look under Marketing Materials',
            detail: 'The sales manual is listed there as a PDF.',
          },
        ],
        related: ['Where can I find marketing materials?'],
      }),
      q('about', 'How do I find project information?', {
        summary: "General project information is on the project's About page.",
        steps: [
          { title: 'Open the project', detail: 'Go to Projects and select one.' },
          {
            title: 'Click About',
            detail:
              'In the left menu, click About for the overview, location and completion details.',
          },
        ],
        related: ['Where can I find marketing materials?'],
      }),
      q('document', 'Where can I find marketing renders?', {
        summary: "Renders are grouped with the images in the project's Documents page.",
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          {
            title: 'Open the Images tab',
            detail: 'Renders are listed alongside other marketing images.',
          },
        ],
        related: ['Where can I find floorplates?', 'Where can I find marketing materials?'],
      }),
      q('floorplan', 'Where can I find floorplates?', {
        summary: "Floorplates are stored with the project's floorplans on the Documents page.",
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          {
            title: 'Open the Floorplans tab',
            detail: 'Floorplates are listed here alongside individual apartment floorplans.',
          },
        ],
        related: ['How do I download floorplans?'],
      }),
      q('list', 'What types of marketing materials are available?', {
        summary:
          'Each project typically has renders, floorplans, the sales manual, brochures and price lists.',
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          {
            title: 'Check each tab',
            detail: 'Materials are organised into Images, Floorplans and Contract tabs.',
          },
        ],
        related: ['Where can I find marketing materials?'],
      }),
      q('export', 'How do I download marketing materials?', {
        summary: 'Any file on the Documents page can be downloaded individually.',
        steps: [
          { title: 'Open the file', detail: 'Go to Documents and find the file you need.' },
          {
            title: 'Click Download',
            detail: "Use the download icon on the file's row or preview.",
          },
        ],
        related: ['Where can I find marketing materials?'],
      }),
    ],
  },
  {
    id: 'contracts',
    icon: 'document-file',
    label: 'Contracts',
    questions: [
      q('document-file', 'Where can I find a Proforma contract?', {
        summary:
          "The Proforma contract is stored under the project's Documents page, in the Contract tab.",
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          { title: 'Open the Contract tab', detail: 'The Proforma contract is listed there.' },
        ],
        related: ['Where can I find EOI form?'],
      }),
      q('document-file', 'Where can I find EOI form?', {
        summary:
          "The EOI form is available on the project's Documents page, alongside the contract.",
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          {
            title: 'Open the Contract tab',
            detail: 'The EOI form is listed there as a downloadable PDF.',
          },
        ],
        related: ['Where can I find a Proforma contract?', 'How do agents submit an EOI?'],
      }),
    ],
  },
  {
    id: 'apartment-info',
    icon: 'apartment-building',
    label: 'Apartment & Project Information',
    questions: [
      q('apartment', 'How do I find information about an apartment?', {
        summary: 'Click any lot in the Apartments table to see its full details.',
        steps: [
          { title: 'Open Apartments', detail: 'Go to the project and click Apartments.' },
          { title: 'Click the lot', detail: 'Select the apartment you want to view.' },
          {
            title: 'Review its details',
            detail:
              'Type, level, price, status, specifications and floorplan all show on its detail panel.',
          },
        ],
        related: ['Can I view apartment specifications and finishes?'],
      }),
      q('export', 'How do I download floorplans?', {
        summary:
          'Floorplans can be downloaded from either the apartment itself or the Documents page.',
        steps: [
          {
            title: 'Open the apartment or Documents',
            detail: "Go to the apartment's detail panel, or Documents › Floorplans.",
          },
          { title: 'Click Download', detail: 'Use the download icon next to the floorplan.' },
        ],
        related: ['Where can I find floorplans?'],
      }),
      q('floorplan', 'Where can I find floorplans?', {
        summary:
          "Floorplans live in two places — the individual apartment, and the project's Documents page.",
        steps: [
          {
            title: 'Per apartment',
            detail: 'Open the lot in Apartments — its floorplan shows on the detail panel.',
          },
          { title: 'All floorplans', detail: 'Or go to Documents › Floorplans for the full set.' },
        ],
        related: ['How do I download floorplans?'],
      }),
      q('color-scheme', 'Can I view apartment specifications and finishes?', {
        summary: "Yes — specifications and finishes are listed on the apartment's detail panel.",
        steps: [
          { title: 'Open the apartment', detail: 'Go to Apartments and click the lot.' },
          {
            title: 'Check Specifications',
            detail: 'Finishes, inclusions and colour scheme show under the Specifications section.',
          },
        ],
        related: ['How do I find information about an apartment?'],
      }),
      q(
        'dollar-circle',
        'Where can I find estimated outgoings (e.g OC fees, council rate, stamp duty)?',
        {
          summary: "Estimated outgoings show on the apartment's detail panel, alongside its price.",
          steps: [
            { title: 'Open the apartment', detail: 'Go to Apartments and click the lot.' },
            {
              title: 'Check the Outgoings section',
              detail: 'Estimated OC fees, council rates and stamp duty are listed there.',
            },
          ],
          related: ['How do I find information about an apartment?'],
        },
      ),
    ],
  },
  {
    id: 'price-lists',
    icon: 'dollar-circle',
    label: 'Price Lists',
    questions: [
      q('dollar-circle', 'Where can I find the latest price list?', {
        summary: "The current price list is on the project's Documents page.",
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          {
            title: 'Open the Contract or Price List tab',
            detail: 'The most recent price list is always at the top.',
          },
        ],
        related: ['Can I download the price list?'],
      }),
      q('dollar-circle', 'Can I download the price list?', {
        summary: 'Yes — download it as a PDF or CSV directly from Documents.',
        steps: [
          { title: 'Open Documents', detail: 'Go to the project and click Documents.' },
          { title: 'Click Download', detail: 'Use the download icon on the price list file.' },
        ],
        related: ['Where can I find the latest price list?'],
      }),
      q('exclamation-circle', 'How do I search for apartments within a specific price range?', {
        summary: 'Use the price filter on the Apartments page to narrow the list.',
        steps: [
          { title: 'Open Apartments', detail: 'Go to the project and click Apartments.' },
          { title: 'Open Filters', detail: 'Click Filter and set a minimum and maximum price.' },
          { title: 'Apply', detail: 'Click Apply — the table updates to match your range.' },
        ],
        related: ['Where can I find the latest price list?'],
      }),
    ],
  },
  {
    id: 'inspections',
    icon: 'calendar-up',
    label: 'Inspections',
    questions: [
      q('calendar-up', 'How do I book inspections?', {
        summary:
          "Inspections are booked by contacting the project's sales team directly — the portal doesn't book them for you.",
        steps: [
          {
            title: 'Find the contact',
            detail: "Open the project's About page for the sales team's contact details.",
          },
          { title: 'Get in touch', detail: 'Call or email them to arrange a time.' },
        ],
        related: ['How do I find project information?'],
      }),
    ],
  },
];

// ---------------------------------------------------------------------------
// Per-page contexts (FEC-1227 — Hina 26 Oct: "the questions do not change
// based on the page I am viewing"). Every step names the page's real button
// and menu labels; pages agents can't open say so rather than invent steps.
// ---------------------------------------------------------------------------

/** One numbered answer step. */
function s(title: string, detail: string): AiHelpQuestion['answer']['steps'][number] {
  return { title, detail };
}

/** "COMMON AGENT QUESTIONS" — the same pair under For Agents on every page (Zeplin screen C). */
export const COMMON_AGENT_QUESTIONS: readonly AiHelpQuestion[] = APARTMENTS_COMMON_AGENT_QUESTIONS;

const CHANGE_PASSWORD = AI_HELP_CATEGORIES[0].questions[2];

/** "Can agents see X?" for the admin-only pages (Allocations, Upgrades, History, …). */
function agentsCannotSee(label: string): AiHelpQuestion {
  return q('user-profile', `Can agents see ${label}?`, {
    summary: `No — ${label} is only shown to developers and master agents, so agents won't find it in their menu.`,
    steps: [
      s('Who can open it', `Developers, developer viewers and master agents see ${label}.`),
      s('What agents use instead', 'Agents work from Apartments, About and Documents.'),
    ],
    related: ['How does an agent reserve an apartment?', "Why can't an agent see a price?"],
  });
}

const AGENT_RESERVE_ONLINE: AiHelpQuestion = q('globe', "Why can't an agent reserve online?", {
  summary: 'Online reservation is switched on per agency, for each project.',
  steps: [
    s('Check the agency', "On the project's Agencies page, look at the Reserve Online column."),
    s(
      'What the agent sees',
      'Without it the apartment shows Call to Reserve instead of Reserve Online.',
    ),
  ],
  related: ['How does an agent reserve an apartment?'],
});

const AGENT_REQUEST_ACCESS: AiHelpQuestion = q(
  'key',
  'How does an agent request access to a project?',
  {
    summary:
      "Agents ask for access from the project's card, and an admin approves it under Requests.",
    steps: [
      s('Open Projects', 'Go to the All Projects or Coming Soon tab.'),
      s('Click Request Access', "Use the button on the project's card."),
      s(
        'Wait for approval',
        'A developer or master agent accepts or rejects it on the Requests page.',
      ),
    ],
    related: ['Where do agents find their projects?'],
  },
);

const AGENT_FIND_PROJECTS: AiHelpQuestion = q(
  'apartment-building',
  'Where do agents find their projects?',
  {
    summary:
      'The Projects page opens on My Project — the projects the agent already has access to.',
    steps: [
      s('My Project', 'Projects the agent can work on.'),
      s('All Projects / Coming Soon', 'Everything else, where they can Request Access.'),
    ],
    related: ['How does an agent request access to a project?'],
  },
);

const AGENT_RESERVATION_STATUS: AiHelpQuestion = q(
  'tick-circle-linear',
  'Where do agents see their reservation status?',
  {
    summary: 'On the apartment itself — its button changes as the reservation moves along.',
    steps: [
      s('Open the apartment', 'Go to Apartments and click the lot.'),
      s('While waiting', 'The button reads Reservation Pending until an admin acts on it.'),
      s('Once approved', 'Agents can use Cancel Reserve if the buyer pulls out.'),
    ],
    related: ['How does an agent reserve an apartment?'],
  },
);

/** "Where do agents see a lot's upgrades / variations / colour scheme?" — the apartment dialog tab. */
function agentsSeeOnLot(thing: string, tab: string): AiHelpQuestion {
  return q('apartment', `Where do agents see a lot's ${thing}?`, {
    summary: `Agents see a lot's ${thing} on the apartment itself, in its ${tab} tab.`,
    steps: [
      s('Open Apartments', "In the project's left menu, click Apartments."),
      s('Click the lot', 'The apartment opens in a dialog.'),
      s(`Open ${tab}`, `The ${tab} tab lists what applies to that lot.`),
    ],
    related: ['What can agents see on this page?'],
  });
}

/** Import / Export questions shared by the catalogue-style pages. */
function importExport(label: string, icon = 'import'): AiHelpQuestion {
  return q(icon, `How do I import or export ${label.toLowerCase()}?`, {
    summary: `Both buttons sit in the ${label} toolbar.`,
    steps: [
      s('Export', `Click Export to download the current ${label.toLowerCase()} list.`),
      s('Import', 'Click Import, choose your file and follow the prompts to confirm.'),
    ],
    tip: 'Import is available to developers and master agents.',
    related: [],
  });
}

type PageContextSpec = {
  readonly label: string;
  readonly icon: string;
  readonly admin: readonly AiHelpQuestion[];
  readonly agent: readonly AiHelpQuestion[];
  readonly categoryIds?: readonly string[];
};

/** Builds a page context: Ask previews the first two agent questions; its own Browse topic leads. */
function pageContext(spec: PageContextSpec): AiHelpPageContext {
  return {
    label: spec.label,
    suggestedAdmin: spec.admin,
    suggestedAgent: spec.agent.slice(0, 2),
    agentsOnPage: spec.agent,
    agentCommonQuestions: COMMON_AGENT_QUESTIONS,
    onThisPageCategoryIds: spec.categoryIds ?? [],
    pageCategory: {
      id: `page-${spec.label.toLowerCase().replace(/[^a-z]+/g, '-')}`,
      icon: spec.icon,
      label: spec.label,
      questions: [...spec.admin, ...spec.agent],
    },
  };
}

const DASHBOARD_CONTEXT = pageContext({
  label: 'Dashboard',
  icon: 'dashboard',
  admin: [
    q('calendar-up', 'How do I change the reporting period?', {
      summary: 'Every Dashboard figure follows the period picked at the top of the page.',
      steps: [
        s('Open the Dashboard', "In the project's left menu, click Dashboard."),
        s(
          'Pick a period',
          'Choose Current Week, Last Week, Current Month, Last Month, Year To Date or All Sales.',
        ),
        s('Read the results', 'The cards and charts on the open tab update to that period.'),
      ],
      related: ['What do the Dashboard tabs show?'],
    }),
    q('export', 'How do I export a project report?', {
      summary: 'Export Report downloads the Dashboard for the selected period.',
      steps: [
        s('Choose the period', 'Set the reporting period first.'),
        s('Click Export Report', 'The button sits at the top right of the Dashboard.'),
      ],
      tip: 'Export Report only shows for users allowed to export reports.',
      related: ['How do I change the reporting period?'],
    }),
    q('presentation-chart', 'What do the Dashboard tabs show?', {
      summary: 'Each tab breaks the project’s sales down a different way.',
      steps: [
        s('Summary', 'Key metrics and recent sales activity.'),
        s(
          'Sales by Product Type / FIRB vs Local / Agent',
          'Sales split by each of those dimensions.',
        ),
        s('Back-to-Market', 'Lots that came back onto the market.'),
      ],
      related: ['How do I export a project report?'],
    }),
  ],
  agent: [
    q('user-profile', 'Can agents see the Dashboard?', {
      summary: 'No — opening a project takes an agent straight to Apartments.',
      steps: [
        s('Agents', 'Land on Apartments and never see the Dashboard.'),
        s('Agency managers', 'See the Summary tab only, filtered to their own agency.'),
      ],
      related: ['Where do agents track their own sales?'],
    }),
    q('sports-ranking', 'Where do agents track their own sales?', {
      summary: 'In the Sales Pipeline, which only ever lists the agent’s own sales.',
      steps: [
        s('Open Sales Pipeline', 'Click Sales Pipeline in the top menu.'),
        s('Narrow it down', 'Use Filter for Projects, Status or Date range.'),
      ],
      related: ['Can agents see the Dashboard?'],
    }),
    AGENT_RESERVE_APARTMENT,
  ],
});

/** Car Spaces and Storage are the same listing with different nouns. */
function extrasContext(
  label: string,
  noun: string,
  addLabel: string,
  icon: string,
  search: string,
) {
  return pageContext({
    label,
    icon,
    categoryIds: ['price-lists'],
    admin: [
      q('plus', `How do I add a ${noun}?`, {
        summary: `${label} are added one at a time from the ${label} page, or in bulk with Import.`,
        steps: [
          s(`Open ${label}`, `In the project's left menu, click ${label}.`),
          s(`Click ${addLabel}`, 'The button sits at the top of the list.'),
          s('Save it', 'Complete the form and save.'),
        ],
        tip: `Import brings in many ${label.toLowerCase()} at once from a spreadsheet.`,
        related: [`How do I edit or delete a ${noun}?`],
      }),
      q('edit', `How do I edit or delete a ${noun}?`, {
        summary: `Each ${noun} row has its own action menu.`,
        steps: [
          s('Find it', `Use the "${search}" search or Filter.`),
          s('Open the row menu', 'Choose Edit to change it, or Delete to remove it.'),
        ],
        related: [`How do I add a ${noun}?`],
      }),
      importExport(label),
    ],
    agent: [
      q('user-profile', `What can agents see on ${label}?`, {
        summary: `Agents with access get a read-only ${label} list.`,
        steps: [
          s('Read-only', "They can search and filter, but can't add, edit, import or export."),
          s('Prices', 'Hidden when the project hides prices from agents.'),
        ],
        related: [`Why can't an agent see ${label}?`],
      }),
      q('lock', `Why can't an agent see ${label}?`, {
        summary: `${label} only appears for agents when the project gives them car-park access.`,
        steps: [
          s(
            'Check project access',
            'A developer or master agent grants it in the project permissions.',
          ),
        ],
        related: [`What can agents see on ${label}?`],
      }),
    ],
  });
}

const CAR_SPACES_CONTEXT = extrasContext(
  'Car Spaces',
  'car space',
  'Add Car Space',
  'car_spaces',
  'Search by level, space...',
);
const STORAGE_CONTEXT = extrasContext(
  'Storage',
  'storage cage',
  'Add Storage Cage',
  'storage',
  'Search by level, storage...',
);

const ABOUT_CONTEXT = pageContext({
  label: 'About',
  icon: 'about',
  categoryIds: ['apartment-info', 'inspections'],
  admin: [
    q('about', "What's on the About page?", {
      summary: "About is the project's overview — key facts, introduction and features.",
      steps: [
        s(
          'Key facts',
          'Apartments, Architect, Levels, Builder, Price Range, EST Completion, Reservation and Website.',
        ),
        s('Introduction & Features', 'A written overview of the project.'),
        s('Configuration table', 'Size and price ranges for each apartment configuration.'),
      ],
      related: ['How do I update the project details?'],
    }),
    q('edit', 'How do I update the project details?', {
      summary: 'Project details are changed with Edit in the project header.',
      steps: [
        s('Open the project', 'Any page inside the project shows the header.'),
        s('Click Edit', 'Next to Duplicate, at the top of the project.'),
        s('Save', 'Make your changes and save the form.'),
      ],
      tip: 'Edit is available to developers and master agents.',
      related: ["What's on the About page?"],
    }),
    q('calendar-up', 'When is the project due for completion?', {
      summary: 'The estimated date is listed on About as EST Completion.',
      steps: [s('Open About', "In the project's left menu, click About.")],
      related: ["What's on the About page?"],
    }),
  ],
  agent: [
    q('user-profile', 'What can agents see on About?', {
      summary: 'Agents see exactly the same About page — it is read-only for everyone.',
      steps: [s('Same content', 'Key facts, introduction, features and the configuration table.')],
      related: ['Where do agents find the project website?'],
    }),
    q('globe', 'Where do agents find the project website?', {
      summary: 'The website link is listed with the key facts on About.',
      steps: [s('Open About', 'Website sits alongside Architect, Builder and the other facts.')],
      related: ['What can agents see on About?'],
    }),
  ],
});

const DOCUMENTS_CONTEXT = pageContext({
  label: 'Documents',
  icon: 'document',
  categoryIds: ['marketing-materials', 'contracts', 'price-lists'],
  admin: [
    q('import', 'How do I upload documents?', {
      summary: 'Files upload into whichever category tab is open.',
      steps: [
        s('Open Documents', "In the project's left menu, click Documents."),
        s('Pick the category', 'Select the category tab the files belong in.'),
        s('Click Upload', 'Choose your files in the Upload files dialog.'),
      ],
      related: ['How do I hide a document from agents?'],
    }),
    q('eye-slash', 'How do I hide a document from agents?', {
      summary: "Hidden documents stay in the library but aren't shown to agents.",
      steps: [
        s('Find the document', 'Open its category tab.'),
        s('Open its menu', 'Choose Hide — Make visible reverses it.'),
      ],
      related: ['How do I upload documents?'],
    }),
    q('history', 'How do I replace a file or see older versions?', {
      summary: "Every document keeps its version history when it's replaced.",
      steps: [
        s('Open its menu', "Use the document's menu."),
        s('Replace File', 'Upload the new version in its place.'),
        s('Version history', 'Shows the earlier versions.'),
      ],
      related: ['How do I upload documents?'],
    }),
  ],
  agent: [
    q('user-profile', 'What can agents see on Documents?', {
      summary:
        "Agents see the visible files and can download them, but can't upload, tag or delete.",
      steps: [
        s('Viewing', 'Only documents that are not hidden.'),
        s('Downloading', 'Needs download access on the project.'),
      ],
      tip: 'Documents only appears for agents when the project gives them document access.',
      related: ['How do agents download a document?'],
    }),
    q('export', 'How do agents download a document?', {
      summary: 'Any visible document can be downloaded on its own.',
      steps: [
        s('Open Documents', "In the project's left menu, click Documents."),
        s('Click Download', 'On the file you need.'),
      ],
      related: ['What can agents see on Documents?'],
    }),
  ],
});

const ANNOUNCEMENTS_CONTEXT = pageContext({
  label: 'Announcements',
  icon: 'announcement',
  admin: [
    q('announcement', 'How do I send an announcement?', {
      summary: "Announcements are written on the project's Announcements page.",
      steps: [
        s('Click New Announcement', 'At the top of Announcements.'),
        s('Choose the type', 'Agent Announcement or Matrix Announcement.'),
        s('Click Send', 'Or Save As Draft to finish it later.'),
      ],
      related: ['How do I edit or delete an announcement?'],
    }),
    q('edit', 'How do I edit or delete an announcement?', {
      summary: 'Each announcement row has its own action menu.',
      steps: [s('Open the row menu', 'Choose View, Edit or Delete.')],
      related: ['How do I send an announcement?'],
    }),
    q('document', 'Can I save an announcement as a draft?', {
      summary: 'Yes — Save As Draft keeps it in the list without sending it.',
      steps: [
        s('Click Save As Draft', 'Instead of Send.'),
        s('Send it later', 'Open it with Edit and click Send.'),
      ],
      related: ['How do I send an announcement?'],
    }),
  ],
  agent: [
    q('bell-notification', 'How do agents see announcements?', {
      summary: 'Unread announcements pop up when an agent opens the project.',
      steps: [
        s('On opening the project', 'Unread announcements appear automatically.'),
        s('Announcements page', 'Lists every announcement for that project.'),
      ],
      related: ['How do agents mark announcements as read?'],
    }),
    q('tick-circle-linear', 'How do agents mark announcements as read?', {
      summary: 'One click clears them all.',
      steps: [s('Open Announcements', 'Click Mark all as read.')],
      related: ['How do agents see announcements?'],
    }),
  ],
});

const ALLOCATIONS_CONTEXT = pageContext({
  label: 'Allocations',
  icon: 'allocation',
  admin: [
    q('allocation', 'How do I allocate stock to an agency?', {
      summary: "Stock is allocated agency by agency from the project's Allocations page.",
      steps: [
        s('Open Allocations', "In the project's left menu, click Allocations."),
        s(
          'Click the pencil',
          "On the agency's row — it opens Allocation of Stock for that agency.",
        ),
        s('Click an Available lot', 'Pick the lots to allocate on the board.'),
      ],
      related: ['How do I unallocate or reallocate a lot?'],
    }),
    q('refresh', 'How do I unallocate or reallocate a lot?', {
      summary: "Changes are made on the agency's allocation board.",
      steps: [
        s("Open the agency's board", 'Click the pencil on its row.'),
        s('Click the lot', 'Choose to unallocate it or move it to another agency.'),
      ],
      related: ['How do I allocate stock to an agency?'],
    }),
    q('search', "How do I find an agency's allocation?", {
      summary: 'Search the Allocations list by agency code or name.',
      steps: [s('Search', 'Type in "Search by code, name...".')],
      related: ['How do I allocate stock to an agency?'],
    }),
  ],
  agent: [agentsCannotSee('Allocations'), AGENT_RESERVE_APARTMENT],
});

const UPGRADES_CONTEXT = pageContext({
  label: 'Upgrades',
  icon: 'upgrade-diamond',
  admin: [
    q('upgrade-diamond', 'How do I add an upgrade?', {
      summary: 'Upgrades are set up in two parts — the upgrade type, then its details.',
      steps: [
        s('Open Upgrades', "In the project's left menu, click Upgrades."),
        s('Click Add Upgrades', 'At the top of the Upgrade Catalogue.'),
        s('Choose what to add', 'Add/Edit Upgrade Type, or Add/Edit Upgrade Details.'),
      ],
      related: ["What's the difference between the two Upgrades tabs?"],
    }),
    q('list', "What's the difference between the two Upgrades tabs?", {
      summary: 'One is the menu of upgrades, the other is what each lot has.',
      steps: [
        s('Upgrade Catalogue', "The project's list of available upgrades."),
        s('Applied Upgrades', 'The upgrades applied to each lot.'),
      ],
      related: ['How do I add an upgrade?'],
    }),
    importExport('Upgrades'),
  ],
  agent: [agentsCannotSee('Upgrades'), agentsSeeOnLot('upgrades', 'Upgrades')],
});

const COLLECTIONS_CONTEXT = pageContext({
  label: 'Collections',
  icon: 'list',
  admin: [
    q('plus', 'How do I add a collection?', {
      summary: 'Collections are created on the Collections tab.',
      steps: [
        s('Open Collections', "In the project's left menu, click Collections."),
        s('Click Add Collection', 'On the Collections tab.'),
      ],
      related: ['How do I assign a collection to a lot?'],
    }),
    q('apartment', 'How do I assign a collection to a lot?', {
      summary: 'Lots are matched to collections on the Collection Allocations tab.',
      steps: [
        s('Open Collection Allocations', 'The second tab on Collections.'),
        s('Click Assign Collection', 'Then pick the lots and the collection.'),
      ],
      related: ['How do I add a collection?'],
    }),
    importExport('Collections'),
  ],
  agent: [agentsCannotSee('Collections'), agentsSeeOnLot('upgrades', 'Upgrades')],
});

const VARIATIONS_CONTEXT = pageContext({
  label: 'Variations',
  icon: 'variations-icon',
  admin: [
    q('plus', 'How do I add a variation type?', {
      summary: 'Variation types are added from the Variations page.',
      steps: [
        s('Open Variations', "In the project's left menu, click Variations."),
        s('Click Add Variation Type', 'It opens Add/Edit Variation Type.'),
      ],
      related: [],
    }),
    importExport('Variations'),
    agentsSeeOnLot('variations', 'Variations'),
  ],
  agent: [agentsCannotSee('Variations'), agentsSeeOnLot('variations', 'Variations')],
});

const COLOUR_SCHEME_CONTEXT = pageContext({
  label: 'Colour Scheme',
  icon: 'color-scheme',
  categoryIds: ['apartment-info'],
  admin: [
    q('plus', 'How do I add a colour scheme type?', {
      summary: 'Colour scheme types are added from the Colour Scheme page.',
      steps: [
        s('Open Colour Scheme', "In the project's left menu, click Colour Scheme."),
        s('Click Add Colour scheme type', 'It opens Add/Edit Colour scheme type.'),
      ],
      related: [],
    }),
    importExport('Colour Schemes'),
    agentsSeeOnLot('colour scheme', 'Colour scheme'),
  ],
  agent: [agentsCannotSee('Colour Scheme'), agentsSeeOnLot('colour scheme', 'Colour scheme')],
});

const PROJECT_AGENCIES_CONTEXT = pageContext({
  label: 'Agencies',
  icon: 'users-twin-profile',
  admin: [
    q('plus', 'How do I give an agency access to this project?', {
      summary: "Agencies are added to a project from the project's Agencies page.",
      steps: [
        s('Open Agencies', "In the project's left menu, click Agencies."),
        s('Click Add Agencies', 'Choose the agencies in the drawer and save.'),
      ],
      related: ["How do I change an agency's project settings?"],
    }),
    q('edit', "How do I change an agency's project settings?", {
      summary: 'Access and Reserve Online are set per agency.',
      steps: [
        s('Open the row menu', "On the agency's row, choose Edit."),
        s('Update and save', 'Change Access or Reserve Online, then save.'),
      ],
      related: ['How do I remove an agency from this project?'],
    }),
    q('trashIcon', 'How do I remove an agency from this project?', {
      summary: 'Removing an agency takes away its access to this project.',
      steps: [s('Open the row menu', "On the agency's row, choose Delete.")],
      related: ['How do I give an agency access to this project?'],
    }),
  ],
  agent: [agentsCannotSee('Agencies'), AGENT_RESERVE_ONLINE],
});

const HISTORY_CONTEXT = pageContext({
  label: 'History',
  icon: 'history',
  admin: [
    q('filter', 'How do I find a change?', {
      summary: 'History records every change made to the project.',
      steps: [
        s('Open History', "In the project's left menu, click History."),
        s('Click Filter', 'Narrow by Type, Field, User or Date range.'),
      ],
      related: ['How do I revert a change?'],
    }),
    q('undo', 'How do I revert a change?', {
      summary: 'A change can be rolled back from its row.',
      steps: [
        s('Find the change', 'Use Filter to locate it.'),
        s('Revert it', 'Use the revert action on its row and confirm with Revert.'),
      ],
      related: ['How do I find a change?'],
    }),
    importExport('History', 'export'),
  ],
  agent: [agentsCannotSee('History'), AGENT_RESERVE_APARTMENT],
});

const RESERVATIONS_CONTEXT = pageContext({
  label: 'Reservations',
  icon: 'menu/calendar',
  categoryIds: ['contracts'],
  admin: [
    q('tick-circle-linear', 'How do I approve or decline a reservation?', {
      summary: 'Reservations are actioned from the Reservations page.',
      steps: [
        s('Open Reservations', 'Click Reservations in the top menu.'),
        s('Open the row menu', 'Choose Approve or Decline.'),
      ],
      related: ['How do I find a reservation?'],
    }),
    q('filter', 'How do I find a reservation?', {
      summary: 'Use the status tabs, Filter and search together.',
      steps: [
        s('Status tabs', 'All, or one status at a time.'),
        s('Filter', 'By Created date, Agency or Project.'),
      ],
      related: ['How do I approve or decline a reservation?'],
    }),
    q('export', 'How do I export reservations?', {
      summary: 'Export downloads the reservations currently listed.',
      steps: [s('Click Export', 'Apply any filters first to export just those.')],
      related: ['How do I find a reservation?'],
    }),
  ],
  agent: [AGENT_RESERVE_APARTMENT, AGENT_RESERVATION_STATUS, AGENT_RESERVE_ONLINE],
});

const REQUESTS_CONTEXT = pageContext({
  label: 'Requests',
  icon: 'key',
  admin: [
    q('key', 'What is a request?', {
      summary: 'A request is an agent asking for access to a project.',
      steps: [
        s('The agent asks', "They click Request Access on the project's card."),
        s('It lands here', 'Pending requests are listed on the Requests page.'),
        s('You decide', 'Accept or Reject it.'),
      ],
      related: ['How do I accept or reject a request?'],
    }),
    q('tick-circle-linear', 'How do I accept or reject a request?', {
      summary: 'Pending requests carry Accept and Reject actions on their row.',
      steps: [
        s('Find the request', 'Open the pending status tab or search for it.'),
        s('Accept or Reject', 'Use the icons on its row.'),
      ],
      related: ['What is a request?'],
    }),
    q('filter', 'How do I find a request?', {
      summary: 'Use the status tabs, Filter and search together.',
      steps: [
        s('Filter', 'By Project or Agency Name.'),
        s('Search', 'Type in "Search by project, agent...".'),
      ],
      related: ['How do I accept or reject a request?'],
    }),
  ],
  agent: [AGENT_REQUEST_ACCESS, AGENT_FIND_PROJECTS],
});

const SALES_PIPELINE_CONTEXT = pageContext({
  label: 'Sales Pipeline',
  icon: 'sports-ranking',
  admin: [
    q('sports-ranking', 'What does the Sales Pipeline show?', {
      summary: 'Every sale across your projects, with running totals.',
      steps: [
        s('Sales in View', 'How many sales match the current filters.'),
        s('Total Value', 'Their combined value.'),
        s('Sales table', 'One row per sale.'),
      ],
      related: ['How do I filter the Sales Pipeline?'],
    }),
    q('filter', 'How do I filter the Sales Pipeline?', {
      summary: 'Filter narrows the table and the totals together.',
      steps: [s('Click Filter', 'Choose Projects, Status, Agency or a Date range.')],
      related: ['How do I export the Sales Pipeline?'],
    }),
    q('export', 'How do I export the Sales Pipeline?', {
      summary: 'Export downloads the sales currently listed.',
      steps: [s('Click Export', 'Apply any filters first to export just those.')],
      related: ['How do I filter the Sales Pipeline?'],
    }),
  ],
  agent: [
    q('user-profile', 'What can agents see in the Sales Pipeline?', {
      summary: 'Agents only see their own sales.',
      steps: [
        s('Their sales only', "Other agents' sales never appear."),
        s('No export', "Export and Columns aren't available to agents."),
      ],
      related: ['How do agents filter their sales?'],
    }),
    q('filter', 'How do agents filter their sales?', {
      summary: 'Agents use the same Filter, without the Agency option.',
      steps: [s('Click Filter', 'Choose Projects, Status or a Date range.')],
      related: ['What can agents see in the Sales Pipeline?'],
    }),
  ],
});

const USERS_CONTEXT = pageContext({
  label: 'Users',
  icon: 'users-twin-profile',
  categoryIds: ['user-management'],
  admin: [
    q('plus', 'How do I add a user?', {
      summary: 'Users are added from the Users page.',
      steps: [
        s('Open Users', 'Click Users in the top menu.'),
        s('Click Add user', 'Complete the form and save.'),
      ],
      related: ['How do I deactivate or reactivate a user?'],
    }),
    q('lock', 'How do I deactivate or reactivate a user?', {
      summary: "Deactivating removes someone's access without deleting them.",
      steps: [
        s('Open the row menu', "On the user's row, choose Deactivate."),
        s('To undo it', 'Find them on the Deactivated tab and choose Activate.'),
      ],
      related: ['How do I find a user?'],
    }),
    q('filter', 'How do I find a user?', {
      summary: 'Use the tabs and Filter together.',
      steps: [s('Tabs', 'All, Active, Pending or Deactivated.'), s('Filter', 'By Agency or Role.')],
      related: ['How do I add a user?'],
    }),
  ],
  agent: [
    q('user-profile', 'Can agents manage users?', {
      summary: "No — agents don't have a Users page.",
      steps: [s("Their agency's users", "Agency managers manage their own agency's users.")],
      related: ['How do I change my password?'],
    }),
    CHANGE_PASSWORD,
  ],
});

const AGENCIES_CONTEXT = pageContext({
  label: 'Agencies',
  icon: 'users-twin-profile',
  admin: [
    q('plus', 'How do I add an agency?', {
      summary: 'Agencies are added from the Agencies page.',
      steps: [
        s('Open Agencies', 'Click Agencies in the top menu.'),
        s('Click Add agency', 'Complete the form and save.'),
      ],
      related: ['How do I edit or delete an agency?'],
    }),
    q('edit', 'How do I edit or delete an agency?', {
      summary: 'Each agency row has its own action menu.',
      steps: [
        s('Find it', 'Search by name, or use Filter.'),
        s('Open the row menu', 'Choose Edit or Delete.'),
      ],
      related: ['How do I add an agency?'],
    }),
    importExport('Agencies'),
  ],
  agent: [agentsCannotSee('Agencies'), AGENT_RESERVE_ONLINE],
});

const PROJECTS_CONTEXT = pageContext({
  label: 'Projects',
  icon: 'apartment-building',
  categoryIds: ['getting-started'],
  admin: [
    q('plus', 'How do I add a new project?', {
      summary: 'New projects start from the Add project button in the header.',
      steps: [
        s('Click Add project', 'At the top of the Projects page.'),
        s('Save it', 'Complete the form and save.'),
      ],
      related: ['How do I duplicate a project?'],
    }),
    q('copy', 'How do I duplicate a project?', {
      summary: 'Duplicate copies an existing project as a starting point.',
      steps: [
        s('Open the project', 'Click it on the Projects page.'),
        s('Click Duplicate', 'In the project header, next to Edit.'),
      ],
      related: ['How do I add a new project?'],
    }),
    q('list', 'How do I find a project?', {
      summary: 'Projects are grouped into tabs.',
      steps: [
        s('Pick a tab', 'Current, Offline, Archive or Draft.'),
        s('Open it', 'Click the project to go to its Dashboard.'),
      ],
      related: ['How do I add a new project?'],
    }),
  ],
  agent: [AGENT_FIND_PROJECTS, AGENT_REQUEST_ACCESS],
});

const DEFAULT_CONTEXT: AiHelpPageContext = {
  label: 'Agent Access',
  suggestedAdmin: AI_HELP_CATEGORIES.find((c) => c.id === 'getting-started')!.questions,
  suggestedAgent: [AGENT_SUBMIT_EOI, AGENT_WHY_NO_PRICE],
  agentsOnPage: [AGENT_RESERVE_APARTMENT, AGENT_REQUEST_ACCESS],
  agentCommonQuestions: COMMON_AGENT_QUESTIONS,
  onThisPageCategoryIds: ['getting-started'],
  pageCategory: null,
};

const APARTMENTS_CONTEXT: AiHelpPageContext = {
  label: 'Apartments',
  suggestedAdmin: APARTMENTS_ADMIN_QUESTIONS,
  suggestedAgent: APARTMENTS_ASK_AGENT_PREVIEW,
  agentsOnPage: APARTMENTS_AGENTS_ON_PAGE,
  agentCommonQuestions: APARTMENTS_COMMON_AGENT_QUESTIONS,
  onThisPageCategoryIds: ['apartment-info', 'price-lists', 'inspections'],
  pageCategory: null,
};

/** `project-detail/:id/<section>` → context. Keys are the route paths in pages/project-details/routes.ts. */
const PROJECT_SECTION_CONTEXTS: Readonly<Record<string, AiHelpPageContext>> = {
  dashboard: DASHBOARD_CONTEXT,
  view: APARTMENTS_CONTEXT,
  'car-spaces': CAR_SPACES_CONTEXT,
  storage: STORAGE_CONTEXT,
  info: ABOUT_CONTEXT,
  documents: DOCUMENTS_CONTEXT,
  announcement: ANNOUNCEMENTS_CONTEXT,
  allocations: ALLOCATIONS_CONTEXT,
  allocation: ALLOCATIONS_CONTEXT,
  upgrades: UPGRADES_CONTEXT,
  collections: COLLECTIONS_CONTEXT,
  variations: VARIATIONS_CONTEXT,
  'color-schemes': COLOUR_SCHEME_CONTEXT,
  'color-scheme': COLOUR_SCHEME_CONTEXT,
  agencies: PROJECT_AGENCIES_CONTEXT,
  'version-control-history': HISTORY_CONTEXT,
};

/** First URL segment → context, for the pages outside a project. */
const TOP_LEVEL_CONTEXTS: Readonly<Record<string, AiHelpPageContext>> = {
  '': PROJECTS_CONTEXT,
  project: PROJECTS_CONTEXT,
  precinct: PROJECTS_CONTEXT,
  reservations: RESERVATIONS_CONTEXT,
  requests: REQUESTS_CONTEXT,
  'sales-pipeline': SALES_PIPELINE_CONTEXT,
  users: USERS_CONTEXT,
  'users-dev': USERS_CONTEXT,
  'agencies-dev': AGENCIES_CONTEXT,
};

/** Every page context, for free-text matching and the data spec. */
export const AI_HELP_PAGE_CONTEXTS: readonly AiHelpPageContext[] = [
  ...new Set([
    ...Object.values(PROJECT_SECTION_CONTEXTS),
    ...Object.values(TOP_LEVEL_CONTEXTS),
    DEFAULT_CONTEXT,
  ]),
];

/** Every mocked question (Browse categories + every page context), de-duplicated by text. */
export const ALL_AI_HELP_QUESTIONS: readonly AiHelpQuestion[] = [
  ...new Map(
    [
      ...AI_HELP_CATEGORIES.flatMap((category) => category.questions),
      ...AI_HELP_PAGE_CONTEXTS.flatMap((context) => [
        ...context.suggestedAdmin,
        ...context.agentsOnPage,
        ...context.agentCommonQuestions,
      ]),
    ].map((question) => [question.text, question] as const),
  ).values(),
];

/**
 * Resolves the "page-aware" context from the current route (FEC-1227). Project
 * pages are keyed on their `project-detail/:id/<section>` segment — Apartments
 * is `view`, which the old `/apartment/` test never matched, so no page ever
 * changed its questions. Anything unmapped (My Account, Settings, …) falls back
 * to the generic Getting Started context.
 */
export function resolveAiHelpPageContext(url: string): AiHelpPageContext {
  const path = url.split(/[?#]/)[0].toLowerCase();

  const section = path.match(/^\/project-detail\/[^/]+(?:\/([^/]+))?/);
  if (section) return PROJECT_SECTION_CONTEXTS[section[1] ?? 'dashboard'] ?? DASHBOARD_CONTEXT;
  if (/^\/project\/[^/]+\/upgrade/.test(path)) return UPGRADES_CONTEXT;

  return TOP_LEVEL_CONTEXTS[path.split('/')[1] ?? ''] ?? DEFAULT_CONTEXT;
}

/** The numeric project id in a `project-detail/:id/...` URL, or 0 outside a project. */
export function aiHelpProjectId(url: string): number {
  const id = Number(url.match(/^\/project-detail\/(\d+)/)?.[1]);
  return Number.isFinite(id) ? id : 0;
}
